import {Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core'
import {
  AddRoomMemberError,
  Capacity,
  MemberType,
  MessageContentInput,
  MessageSortDirection,
  Messaging_AddMemberGql,
  Messaging_GenerateFileUploadCoordinatesGql,
  Messaging_ListRoomsForProUserRelatedToJobGql,
  Messaging_ListRoomsForUserGql,
  Messaging_ListRoomsForUserRelatedToJobGql,
  Messaging_LoadRoomGql,
  Messaging_MessageFragment,
  Messaging_MoveUnreadCursorGql,
  Messaging_OnMessageReceivedGql,
  Messaging_RemoveMemberGql,
  Messaging_RoomByIdGql,
  Messaging_RoomFragment,
  Messaging_RoomLastMessageFragment,
  Messaging_SendMessageGql,
  RemoveRoomMemberError,
} from '../../../__generated__/types'
import {pickProperty} from '../graphql/graphql-utils'
import {EMPTY, firstValueFrom, from, Observable, of, Subscription, switchMap, throwError} from 'rxjs'
import {catchError, concatMap, map, take, tap} from 'rxjs/operators'
import {GraphqlLinkController} from '../graphql/graphql-link.controller'
import {Logger} from '../logging/logger.types'
import {
  ChatMemberCapacity,
  Message,
  MessageContent,
  MessageUI,
  Room,
  RoomDetails,
  RoomLastMessage,
  RoomLastMessageContent,
  SendMessageResult,
} from './messaging.types'
import {FileModel} from '../../craft/files/file.model'
import {FileService} from '../../craft/files/file.service'
import {AuthStatusService} from '../auth/auth-status.service'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import {MessageStore} from './store/message.store'
import {isPlatformBrowser} from '@angular/common'
import {RoomStore} from './store/room.store'
import {RoomDetailStore} from './store/room-detail.store'
import {GraphqlWebsocketStatusService} from '../graphql/graphql-websocket-status.service'

@Injectable({
  providedIn: 'root',
})
export class MessagingService implements OnDestroy {
  private reconnectionAttempts = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined
  private messageSubscription: Subscription | undefined
  private onSuccessfulConnection: Subscription | undefined
  private readonly MAX_RECONNECT_ATTEMPTS = 15

  constructor(
    private readonly roomStore: RoomStore,
    private readonly roomDetailStore: RoomDetailStore,
    private readonly messageStore: MessageStore,
    private readonly authStatusService: AuthStatusService,
    private readonly graphqlLinkController: GraphqlLinkController,
    private readonly graphqlWebsocketService: GraphqlWebsocketStatusService,
    private readonly fileService: FileService,
    private readonly listRoomsForUserGql: Messaging_ListRoomsForUserGql,
    private readonly listRoomsForUserRelatedToJobGql: Messaging_ListRoomsForUserRelatedToJobGql,
    private readonly loadRoomGql: Messaging_LoadRoomGql,
    private readonly roomByIdGql: Messaging_RoomByIdGql,
    private readonly sendMessageGql: Messaging_SendMessageGql,
    private readonly addMemberGql: Messaging_AddMemberGql,
    private readonly removeMemberGql: Messaging_RemoveMemberGql,
    private readonly generateFileUploadCoordinatesGql: Messaging_GenerateFileUploadCoordinatesGql,
    private readonly moveUnreadCursorGql: Messaging_MoveUnreadCursorGql,
    private readonly onMessageReceived: Messaging_OnMessageReceivedGql,
    private readonly logger: Logger,
    @Inject(PLATFORM_ID) private readonly platformId: Record<string, unknown>,
    private readonly listRoomsForProRelatedToJobGql: Messaging_ListRoomsForProUserRelatedToJobGql,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      // subscribe to new messages to propagate state changes from here
      this.authStatusService.authenticatedUser$.pipe(takeUntilDestroyed()).subscribe(({authenticatedUser}) => {
        if (authenticatedUser) {
          // clean up potential previous subscription
          this.unsubscribeFromMessages()
          this.subscribeToMessages(authenticatedUser.userId)
        } else {
          this.unsubscribeFromMessages()
          this.clearState()
        }
      })
    }
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.unsubscribeFromMessages()
    this.clearState()
  }

  /**
   * Observable that emits the current state of all rooms for a user.
   * @warn this is useful for observing state, but won't actively load the list of rooms from the server. For that, use loadAllRooms$.
   */
  rooms$(): Observable<Room[]> {
    return this.roomStore.allRooms$()
  }

  /**
   * Observable that emits the current number of unread messages in all rooms for a user.
   * @warn this is useful for observing state, but won't actively load the rooms from the server. For that, use loadAllRooms$.
   */
  unreadCount$(): Observable<number> {
    return this.rooms$().pipe(map(rooms => rooms.reduce((acc, room) => acc + room.unreadCount, 0)))
  }

  /**
   * Actively loads all rooms for a user, and returns an observable that will emit when any room changes.
   */
  loadAllRooms$(userId: string) {
    this.logger.debug(`Loading all rooms for user: ${userId}`)
    return this.listRoomsForUserGql
      .fetch(
        {userId},
        {
          fetchPolicy: 'network-only',
        },
      )
      .pipe(
        map(res => pickProperty(res.data, 'listRooms')),
        map(rooms => rooms.map(room => this.fromGqlRoom(room))),
        tap(rooms => this.roomStore.setRooms(rooms)),
        switchMap(() => this.rooms$()), // hand over the observable on the store
      )
  }

  /**
   * Actively load a single room.
   */
  loadRoom(roomId: string) {
    return this.loadRoomGql
      .fetch(
        {id: roomId},
        {
          fetchPolicy: 'network-only',
        },
      )
      .pipe(
        map(res => pickProperty(res.data, 'roomById')),
        map(room => this.fromGqlRoom(room)),
        tap(room => this.roomStore.upsertRooms([room])),
      )
  }

  /**
   * Observable that emits the current state of all rooms related to a job for a user.
   * @warn this is useful for observing state, but won't actively load the list of rooms from the server. For that, use listRoomsForUser.
   */
  roomsRelatedToJob$(jobId: string): Observable<Room[]> {
    return this.roomStore.allRoomsRelatedToJob$(jobId)
  }

  unreadCountRelatedToJob(jobId: string): Observable<number> {
    return this.roomsRelatedToJob$(jobId).pipe(map(rooms => rooms.reduce((acc, room) => acc + room.unreadCount, 0)))
  }

  /**
   * Actively loads all the room related to a job for a user, and returns an observable that will emit when any room changes.
   */
  loadRoomsRelatedToJob$(jobId: string): Observable<Room[]> {
    return this.listRoomsForUserRelatedToJobGql
      .fetch(
        {jobId},
        {
          fetchPolicy: 'no-cache',
        },
      )
      .pipe(
        map(res => pickProperty(res.data, 'listRoomsRelatedToJob')),
        map(rooms => rooms.map(room => this.fromGqlRoom(room))),
        tap(rooms => this.roomStore.upsertRooms(rooms)),
        switchMap(() => this.roomsRelatedToJob$(jobId)), // hand over the observable on the store
      )
  }

  /**
   * Actively loads all the room related to a job for a pro, and returns an observable that will emit when any room changes.
   */
  loadRoomsForProUserRelatedToJob$(jobId: string, proId: string): Observable<Room[]> {
    return this.listRoomsForProRelatedToJobGql
      .fetch(
        {jobId, proId},
        {
          fetchPolicy: 'no-cache',
        },
      )
      .pipe(
        map(res => pickProperty(res.data, 'listRoomsRelatedToJobAndProUser')),
        map(rooms => rooms.map(room => this.fromGqlRoom(room))),
        tap(rooms => this.roomStore.upsertRooms(rooms)),
        switchMap(() => this.roomsRelatedToJob$(jobId)), // hand over the observable on the store
      )
  }

  /**
   * Actively load a room details and all its messages, returning a pair of observables that will emit when the room or messages change.
   * For getting notifications when messages are updated, you can also use messagesInRoom$ and roomDetail$.
   */
  loadRoomDetailWithMessages$(roomId: string) {
    return this.roomByIdGql
      .fetch(
        {roomId, sortDirection: MessageSortDirection.Desc},
        {
          fetchPolicy: 'no-cache',
        },
      )
      .pipe(
        map(res => pickProperty(res.data, 'roomById')),
        map(res => {
          return {
            ...res,
            // messages come in descending order, but the store expects ascending order to facilitate adding new messages
            messages: res.messages.map(m => this.fromGqlMessage(m)).reverse(),
          }
        }),
        tap(room => {
          this.roomDetailStore.upsertRoom(room)
          this.messageStore.setRoomMessages(roomId, room.messages)
        }),
        map(() => ({
          roomDetail$: this.roomDetail$(roomId),
          messages$: this.messagesInRoom$(roomId),
        })),
      )
  }

  roomDetail$(roomId: string): Observable<RoomDetails | undefined> {
    return this.roomDetailStore.roomDetailById$(roomId)
  }

  lastActiveRoom(): string | undefined {
    return this.roomDetailStore.getLastOpenedRoom()
  }

  setActiveRoom(roomId: string) {
    this.roomStore.setActiveRoom(roomId)
    this.roomDetailStore.setActiveRoom(roomId)
  }

  resetActiveRoom() {
    this.roomStore.resetActiveRoom()
    this.roomDetailStore.resetActiveRoom()
  }

  /**
   * Observable that emits the current state of all messages related to a room.
   * @warn this is useful for observing state, but won't actively load the list of messages from the server. For that, use loadRoomDetailsWithMessages$.
   */
  messagesInRoom$(roomId: string): Observable<(Message & MessageUI)[]> {
    return this.messageStore.allMessagesInRoom$(roomId)
  }

  sendSimpleTextMessage(roomId: string, content: {text: string}): Promise<SendMessageResult> {
    const {id} = this.addOptimisticMessage(roomId, {simple: content})
    return this.sendMessage(roomId, {simple: content}, id)
  }

  resendSimpleTextMessage(roomId: string, message: Message) {
    this.messageStore.upsertMessageBeingSent(message)
    return this.sendMessage(roomId, {simple: message.content.simple}, message.id)
  }

  sendFileMessage(roomId: string, file: FileModel, blob: File, companionText?: string): Promise<SendMessageResult> {
    const {id} = this.addOptimisticMessage(roomId, {file: {...file, companionText}}, blob)
    return this.uploadAndSendFile(roomId, id, file, blob, companionText)
  }

  resendFileMessage(roomId: string, message: Message, file: FileModel, blob: File, companionText?: string) {
    this.messageStore.upsertMessageBeingSent(message, blob)
    return this.uploadAndSendFile(roomId, message.id, file, blob, companionText)
  }

  private uploadAndSendFile(
    roomId: string,
    optimisticMessageId: string,
    file: FileModel,
    blob: File,
    companionText?: string,
  ) {
    return firstValueFrom(
      this.fileService
        .uploadFile(file, blob, file => this.generateFileUploadCoordinates(roomId, file))
        .pipe(
          switchMap(file => {
            if (file.error || !file.uploaded) {
              return throwError(() => new Error('Failed to upload file'))
            }
            return this.sendMessage(
              roomId,
              {
                file: {
                  contentType: file.contentType,
                  fileKey: file.fileKey,
                  fileName: blob.name,
                  dimensions: file.dimensions,
                  companionText,
                },
              },
              optimisticMessageId,
            )
          }),
          catchError(err => {
            this.messageStore.setMessageFailed(optimisticMessageId, blob)
            return of({operation: 'failed' as const, error: {message: err.message || 'Failed to upload file'}})
          }),
        ),
    )
  }

  private generateOptimisticId() {
    try {
      return crypto.randomUUID()
    } catch {
      return Math.random().toString(36)
    }
  }

  private addOptimisticMessage(roomId: string, content: MessageContent, blob?: File) {
    const sender = this.authStatusService.me()!
    const optimisticMessage: Message & MessageUI = {
      status: 'sending',
      id: this.generateOptimisticId(),
      roomId,
      sentAt: new Date(),
      sender: {
        id: sender.userId,
        firstName: sender.firstName,
        lastName: sender.lastName,
        type: MemberType.Member,
      },
      content,
      read: true,
      onlyVisibleToUserCapacity: false,
    }
    this.messageStore.upsertMessageBeingSent(optimisticMessage, blob)
    this.roomDetailStore.updateUnreadCursor(roomId, undefined) // reset unread cursor as user has now sent a message
    return optimisticMessage
  }

  private sendMessage(roomId: string, content: MessageContentInput, optimisticId: string): Promise<SendMessageResult> {
    return firstValueFrom(
      this.sendMessageGql.mutate({message: {roomId, content, optimisticId}}).pipe(
        map(res => pickProperty(res.data, 'sendMessage')),
        map(res => ({
          operation: 'success' as const,
          message: this.fromGqlMessage(res),
        })),
        tap(res => this.messageStore.addSentMessage(res.message, optimisticId)),
        catchError(err => {
          this.logger.error('Failed to send message', err)
          this.messageStore.setMessageFailed(optimisticId)
          return of({operation: 'failed' as const, error: {message: 'toString' in err ? err.toString() : err}})
        }),
      ),
    )
  }

  private subscribeToMessages(userId: string) {
    this.messageSubscription = from(this.connectToWebsocket())
      .pipe(
        tap(() => this.logger.debug('connected to websocket')),
        switchMap(() => this.onMessageReceived.subscribe({userId})),
        tap(res => this.logger.debug('subscription to new message got event', {res})),
        map(res => pickProperty(res.data, 'onMessageReceivedByUserId')),
        map(event => ({
          message: this.fromGqlMessage(event.message),
          optimisticId: event.optimisticId,
        })),
      )
      .subscribe({
        next: event => {
          try {
            this.messageStore.upsertReceivedMessage(event.message, event.optimisticId)
            this.loadOrUpdateRoomForNewMessage(event.message)
            this.updateActiveRoomUnreadCursorForNewMessage(event.message)

            // Reset retry counter on successful message
            this.reconnectionAttempts = 0
            this.logger.debug('Successful subscription to pubsub message, resetting reconnection attempts')
          } catch (e) {
            this.logger.error('Failed to process new message', e)
            this.scheduleReconnect()
          }
        },
        complete: () => {
          this.logger.debug('subscription to new messages completed')
        },
        error: err => {
          this.logger.debug('subscription to new messages errored', err)
          this.scheduleReconnect()
        },
      })
  }

  /**
   * Reconnects to websocket immediately if not already connected, skipping any pending reconnection.
   */
  requestImmediateReconnection() {
    // only reconnect if not already connected
    // we might be in the middle of connecting, which is why we can not rely on the pending timeout on its own
    if (!this.graphqlWebsocketService.isConnected()) {
      // clear the pending reconnection, we should only clear it when we actually reconnect
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = undefined
        this.reconnectionAttempts = 0
      }

      this.reconnectToMessages()
    }
  }

  private reconnectToMessages() {
    const currentUser = this.authStatusService.me()
    if (!currentUser) {
      this.logger.debug('reconnecting skipped, current user is null')
      return
    }

    this.logger.debug('reconnecting')
    this.unsubscribeFromMessages()
    this.subscribeToMessages(currentUser.userId)

    this.onSuccessfulConnection = this.graphqlWebsocketService.successfullyConnected$
      .pipe(
        // reload potentially missed messages
        concatMap(() => this.loadAllRooms$(currentUser.userId).pipe(take(1))),
        concatMap(() => {
          const activeRoom = this.roomDetailStore.getActiveRoom()
          return activeRoom ? this.loadRoomDetailWithMessages$(activeRoom.id).pipe(take(1)) : EMPTY
        }),
        catchError(err => {
          this.logger.error('Failed to reload data after reconnection', err)
          return EMPTY
        }),
      )
      .subscribe()
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = undefined
    }

    if (this.reconnectionAttempts > this.MAX_RECONNECT_ATTEMPTS) {
      this.logger.error('Max reconnection retries exceeded, stopping reconnection attempts')
      return
    }

    // retry after a delay to not spam the server
    this.reconnectTimer = setTimeout(
      () => {
        this.logger.debug(
          `retrying connection attempt... [${this.reconnectionAttempts}/${this.MAX_RECONNECT_ATTEMPTS}]`,
        )
        this.reconnectToMessages()
      },
      Math.min(this.reconnectionAttempts, 120) * 500,
    )
    this.reconnectionAttempts++
  }

  private loadOrUpdateRoomForNewMessage(message: Message) {
    if (!this.roomStore.hasRoom(message.roomId)) {
      // room not yet known, load it
      this.loadRoom(message.roomId).pipe(take(1)).subscribe()
    } else {
      // room known, update last message
      this.roomStore.upsertRoomWithNewLastMessage(message.roomId, message)
    }
  }

  private updateActiveRoomUnreadCursorForNewMessage(message: Message) {
    if (message.read) return

    const activeRoomDetails = this.roomDetailStore.getActiveRoom()
    if (activeRoomDetails && !activeRoomDetails.unreadCursor) {
      this.roomDetailStore.updateUnreadCursor(activeRoomDetails.id, message.id)
    }
  }

  private unsubscribeFromMessages() {
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe()
      this.messageSubscription = undefined
    }
    if (this.onSuccessfulConnection) {
      this.onSuccessfulConnection.unsubscribe()
      this.onSuccessfulConnection = undefined
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = undefined
    }
  }

  addRoomMember$(
    roomId: string,
    userId: string,
    capacity: ChatMemberCapacity,
  ): Observable<{room: RoomDetails; error?: never} | {error: AddRoomMemberError; room?: never}> {
    return this.addMemberGql.mutate({roomId, userId, capacity: Capacity[capacity]}).pipe(
      map(r => {
        if (r.data?.addRoomMember.__typename === 'Room') {
          return {room: r.data?.addRoomMember}
        } else if (r.data?.addRoomMember.__typename === 'AddRoomMemberInputError') {
          this.logger.warn(r.data.addRoomMember.message)
          return {error: r.data.addRoomMember.error}
        } else {
          throw new Error(`RoomId ${roomId} and ${userId} not found`)
        }
      }),
      tap(({room}) => {
        if (room) {
          this.roomDetailStore.upsertRoom(room)
        }
      }),
    )
  }

  removeRoomMember$(
    roomId: string,
    userId: string,
    removingSelf: boolean,
  ): Observable<{room: RoomDetails; error?: never} | {error: RemoveRoomMemberError; room?: never}> {
    return this.removeMemberGql.mutate({roomId, userId}).pipe(
      map(r => {
        if (r.data?.removeRoomMember.__typename === 'Room') {
          return {room: r.data.removeRoomMember}
        } else if (r.data?.removeRoomMember.__typename === 'RemoveRoomMemberInputError') {
          this.logger.warn(r.data.removeRoomMember.message)
          return {error: r.data.removeRoomMember.error}
        } else {
          throw new Error(`RoomId ${roomId} and ${userId} not found`)
        }
      }),
      tap(({room}) => {
        if (room) {
          this.roomDetailStore.upsertRoom(room)
          if (removingSelf) {
            this.roomStore.removeRoom(room.id)
            this.roomDetailStore.removeRoom(room.id)
          }
        }
      }),
    )
  }

  moveUnreadCursor(roomId: string, mostRecentlyReadMessageId: string) {
    return firstValueFrom(
      this.moveUnreadCursorGql.mutate({roomId, mostRecentlyReadMessageId}).pipe(
        map(res => {
          if (res.data?.moveUnreadCursor.__typename === 'Room') {
            return {
              roomId: res.data.moveUnreadCursor.id,
              unreadCount: res.data.moveUnreadCursor.unreadCount,
            }
          } else {
            throw new Error(`Failed to move unread cursor for room ${roomId}`)
          }
        }),
        tap(res => this.roomStore.updateRoomUnreadCount(res.roomId, res.unreadCount)),
      ),
    )
  }

  private generateFileUploadCoordinates(
    roomId: string,
    file: {fileName: string; contentType: string; contentLength: number},
  ) {
    return this.generateFileUploadCoordinatesGql
      .mutate({roomId, file})
      .pipe(map(res => pickProperty(res.data, 'generateChatMessageFileUploadLink')))
  }

  private fromGqlLastMessage(message: Messaging_RoomLastMessageFragment): RoomLastMessage {
    function fromGqlContent(): RoomLastMessageContent {
      const {content} = message
      if (content.__typename === 'SimpleTextMessageContent') {
        return {simple: {text: content.text}}
      } else if (content.__typename === 'FileMessageContent') {
        return {file: {contentType: content.file.contentType, companionText: content.companionText}}
      } else if (content.__typename === 'StructuredMessageContent') {
        return {structure: {type: content.type, data: content.data}}
      } else {
        throw new Error('Unknown chat message content type')
      }
    }

    return {
      ...message,
      content: fromGqlContent(),
    }
  }

  private fromGqlMessage(message: Messaging_MessageFragment): Message {
    function fromGqlContent(): MessageContent {
      const {content} = message
      if (content.__typename === 'SimpleTextMessageContent') {
        return {simple: {text: content.text}}
      } else if (content.__typename === 'FileMessageContent') {
        return {
          file: {
            contentType: content.file.contentType,
            url: content.file.url,
            uploaded: true,
            fileKey: content.file.fileKey,
            originalFilename: content.file.name,
            dimensions: content.file.dimensions,
            companionText: content.companionText,
          },
        }
      } else if (content.__typename === 'StructuredMessageContent') {
        return {structure: {type: content.type, data: content.data}}
      } else {
        throw new Error('Unknown chat message content type')
      }
    }
    return {
      ...message,
      content: fromGqlContent(),
    }
  }

  private fromGqlRoom(room: Messaging_RoomFragment): Room {
    return {
      ...room,
      lastMessage: room.lastMessage.length ? this.fromGqlLastMessage(room.lastMessage[0]) : undefined,
    }
  }

  private async connectToWebsocket() {
    await this.graphqlLinkController.addPubSubSupport()
  }

  private clearState() {
    this.roomStore.reset()
    this.roomDetailStore.reset()
    this.messageStore.reset()
  }
}
